import { useMemo } from "react";
import { CohortData, CohortSegment, CohortInsight } from "@/types/cohort.types";

interface UseCohortInsightsProps {
  cohorts: CohortData[];
  segments: CohortSegment[];
}

export const useCohortInsights = ({ cohorts, segments }: UseCohortInsightsProps): CohortInsight[] => {
  return useMemo(() => {
    const insights: CohortInsight[] = [];

    if (cohorts.length < 2) return insights;

    // Compare latest cohort vs previous
    const latest = cohorts[0];
    const previous = cohorts[1];

    if (latest && previous) {
      const conversionDiff = latest.conversionRate - previous.conversionRate;
      
      if (Math.abs(conversionDiff) > 0.5) {
        insights.push({
          type: conversionDiff > 0 ? 'success' : 'warning',
          message: `Neueste Kohorte zeigt ${conversionDiff > 0 ? '+' : ''}${conversionDiff.toFixed(1)}% ${conversionDiff > 0 ? 'bessere' : 'schlechtere'} Conversion`,
          metric: 'conversion_rate',
          value: conversionDiff
        });
      }
    }

    // Find best performing segment
    if (segments.length > 0) {
      const bestSegment = segments[0];
      const avgConversion = segments.reduce((sum, s) => sum + s.conversionRate, 0) / segments.length;
      
      if (bestSegment.conversionRate > avgConversion * 1.2) {
        insights.push({
          type: 'success',
          message: `${bestSegment.name} konvertiert am besten (${bestSegment.conversionRate.toFixed(1)}%)`,
          metric: 'best_segment',
          value: bestSegment.conversionRate
        });
      }
    }

    // Check retention trend
    if (cohorts.length >= 3) {
      const recentCohorts = cohorts.slice(0, 3);
      const avgRetentionWeek1 = recentCohorts.reduce((sum, c) => {
        const week1 = c.retention.find(r => r.period === 1);
        return sum + (week1?.retentionRate || 0);
      }, 0) / recentCohorts.length;

      if (avgRetentionWeek1 > 40) {
        insights.push({
          type: 'success',
          message: `Starke Retention: ${avgRetentionWeek1.toFixed(0)}% der User kehren in Woche 1 zurück`,
          metric: 'retention',
          value: avgRetentionWeek1
        });
      } else if (avgRetentionWeek1 < 25) {
        insights.push({
          type: 'warning',
          message: `Niedrige Retention: Nur ${avgRetentionWeek1.toFixed(0)}% kehren in Woche 1 zurück`,
          metric: 'retention',
          value: avgRetentionWeek1
        });
      }
    }

    // Compare segment time to conversion
    if (segments.length > 1) {
      const fastestSegment = segments.reduce((fastest, s) => 
        (s.avgTimeToConversion || Infinity) < (fastest.avgTimeToConversion || Infinity) ? s : fastest
      );
      
      if (fastestSegment.avgTimeToConversion && fastestSegment.avgTimeToConversion < 2) {
        insights.push({
          type: 'info',
          message: `${fastestSegment.name} konvertiert am schnellsten (${fastestSegment.avgTimeToConversion.toFixed(1)} Tage)`,
          metric: 'time_to_conversion',
          value: fastestSegment.avgTimeToConversion
        });
      }
    }

    return insights;
  }, [cohorts, segments]);
};
